/**
 * Contrato e chamadas do cofre de credenciais (spec §2.4).
 *
 * Quatro regras moram aqui, e nenhuma é detalhe de serialização:
 *
 * · **Nada que carregue valor em claro passa pelo react-query.** Guardar, rotacionar e revelar
 *   são funções soltas, não `useMutation`. O motivo é concreto: o react-query guarda `variables`
 *   **e** `data` de cada mutação no `MutationCache`, que sobrevive à desmontagem do componente
 *   e só é coletado depois do `gcTime`. Usar `useMutation` aqui deixaria a senha do banco do ERP
 *   do cliente viva num cache global do navegador por minutos depois de a tela ter sumido — que
 *   é exatamente o que o ADR 0006 proíbe do outro lado do fio. Quem chama essas funções mantém o
 *   valor numa `useState` de vida curta e o apaga; ver `RevelacaoDoSegredo`.
 *
 * · **A listagem não tem campo de valor**, e isso não é omissão: `esquemaSegredo` não declara
 *   `valor`, `ciphertext` nem `dek_cifrada`, e o zod **descarta chave desconhecida**. Se um dia
 *   uma rota do portal passar a devolver material por engano, ele não chega ao DOM por aqui — a
 *   ausência é a tranca, não a disciplina de quem escreve a tela.
 *
 * · **A tela não decide quem revela.** `podeNaSessao(sessao, "credencial:revelar")` existe para
 *   não OFERECER um botão que o servidor vai negar; a negativa de verdade é a do serviço, que é
 *   quem tem a sessão de banco e a linha de `segredo_revelacao`. Esconder o botão é cortesia,
 *   não segurança — o ADR 0006 é literal: *"tela é encaminhamento"*.
 *
 * · **A custódia é dado, não rodapé.** `sob_custodia_provisoria` vem por segredo e o estado do
 *   portal vem por `GET /cofre/custodia`. Um cofre cuja chave mestra mora num arquivo do host
 *   protege menos do que a spec §5.4 promete, e quem opera precisa ver isso sem procurar.
 */

import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ErroDaApi, apagar, atualizarJson, enviarJson, pedirJson } from "./api";

/* ------------------------------------------------------------------ vocabulário */

/** Os quatro estados da custódia da chave mestra, iguais aos de `cofre/cripto.py`. */
export const ESTADOS_DA_CUSTODIA = [
  "desligado",
  "provisoria",
  "gerenciada",
  "indisponivel",
] as const;

const esquemaEstadoDaCustodia = z.enum(ESTADOS_DA_CUSTODIA);
/* A custódia de UM segredo só tem duas formas: ou a DEK dele foi embrulhada pelo serviço
   gerenciado, ou pela chave derivada de variável de ambiente. `desligado` e `indisponivel` são
   estados do portal, nunca de uma linha — não existe segredo gravado sem chave. */
const esquemaCustodiaDoSegredo = z.enum(["gerenciada", "provisoria"]);

export type EstadoDaCustodia = z.infer<typeof esquemaEstadoDaCustodia>;
export type CustodiaDoSegredo = z.infer<typeof esquemaCustodiaDoSegredo>;

/**
 * A permissão que estas telas consultam, por constante em vez de literal solto.
 *
 * É só esta. `infra:responder` — a de guardar — não aparece em nenhuma decisão de tela: pela
 * matriz da spec §4 ela é do admin do cliente e do TI, então o campo com cadeado do formulário
 * do cliente já está atrás dela por construção, e o console interno nunca a tem. Declará-la aqui
 * criaria a impressão de que alguma tela a verifica.
 */
export const PERMISSAO_DE_REVELAR = "credencial:revelar";

/* Os limites do motivo, iguais aos do servidor (`modelos.py` e `servico.py`). Repetidos aqui
   para a tela poder dizer o que falta ANTES de gastar uma ida ao servidor — nunca para decidir
   sozinha: quem recusa o motivo curto é o CHECK `ck_segredo_revelacao_motivo_preenchido`. */
export const MOTIVO_MINIMO = 10;
export const MOTIVO_MAXIMO = 500;

/* ------------------------------------------------------------------- esquemas */

const esquemaPessoa = z.object({
  id: z.string().min(1),
  nome: z.string().min(1),
  email: z.string().min(1),
});

/**
 * Uma credencial na listagem: rótulo, procedência, datas e quantas vezes já foi vista.
 *
 * **Não existe campo de valor, e nem poderia.** Um campo opcional "preenchido só quando pode" é
 * um campo que um dia é preenchido quando não podia. O único lugar do portal onde uma credencial
 * em claro chega ao navegador é `esquemaValorRevelado`, e para chegar lá uma linha de
 * `segredo_revelacao` já foi gravada com quem, quando, por quê e de onde.
 */
export const esquemaSegredo = z.object({
  id: z.string().min(1),
  rotulo: z.string().min(1),
  /* De qual pergunta do formulário a credencial nasceu. Nulo = registrada por fora, pelo
     console — é o que separa "o cliente entregou" de "alguém digitou aqui dentro". */
  origem_pergunta_id: z.string().nullable(),
  algoritmo: z.string().min(1),
  custodia: esquemaCustodiaDoSegredo,
  sob_custodia_provisoria: z.boolean(),
  /* `chave_mestra_ref` NÃO entra aqui, e a ausência é o conserto de um vazamento: a listagem
     é servida com `infra:ver`, que `admin_cliente` e `ti` — papéis do cliente — têm. A
     referência é a impressão digital da chave mestra do PORTAL, a mesma para todas as
     empresas, e a API só a devolve em `GET /api/cofre/custodia`, com `console:ver`. O que
     esta tela precisa dizer sobre a credencial é `custodia`/`sob_custodia_provisoria`. */
  criado_em: z.string(),
  criado_por: esquemaPessoa.nullable(),
  rotacionado_em: z.string().nullable(),
  rotacionado_por: esquemaPessoa.nullable(),
  expira_em: z.string().nullable(),
  expirado: z.boolean(),
  descartado_em: z.string().nullable(),
  descartado_por: esquemaPessoa.nullable(),
  descartado: z.boolean(),
  revelacoes: z.number(),
  ultima_revelacao_em: z.string().nullable(),
});

export const esquemaCofre = z.object({
  implantacao_id: z.string().min(1),
  segredos: z.array(esquemaSegredo),
  total: z.number(),
  vivos: z.number(),
  sob_custodia_provisoria: z.number(),
});

/**
 * A única resposta do portal que carrega credencial em claro.
 *
 * `revelacao_id` e `ocorrido_em` voltam junto de propósito: é o que permite à tela dizer, na
 * mesma hora, que o acesso ficou registrado. Registro que quem usa não vê acontecer é registro
 * que ele esquece que existe.
 */
const esquemaValorRevelado = z.object({
  segredo_id: z.string().min(1),
  rotulo: z.string().min(1),
  valor: z.string(),
  revelacao_id: z.string().min(1),
  ocorrido_em: z.string(),
  sob_custodia_provisoria: z.boolean(),
});

export const esquemaCustodia = z.object({
  estado: esquemaEstadoDaCustodia,
  /* O identificador da chave — o id da gerenciada ou a impressão digital da provisória. Nunca
     material. Ele NÃO sai em `/api/saude`, que é público; sai aqui, atrás de `console:ver`. */
  referencia: z.string(),
  detalhe: z.string(),
  operante: z.boolean(),
  exige_atencao: z.boolean(),
  segredos_vivos: z.number(),
  segredos_sob_custodia_provisoria: z.number(),
});

export type SegredoDoCofre = z.infer<typeof esquemaSegredo>;
export type CofreDaImplantacao = z.infer<typeof esquemaCofre>;
export type ValorRevelado = z.infer<typeof esquemaValorRevelado>;
export type CustodiaDoCofre = z.infer<typeof esquemaCustodia>;

/* -------------------------------------------------------------------- consultas */

/**
 * A raiz das chaves do cofre: a custódia e a listagem de cada implantação.
 *
 * Guardar, rotacionar, descartar e revelar mudam o placar da custódia **e** a linha da listagem
 * (o contador de revelações), então quem escreve invalida a raiz em vez de escolher a chave certa
 * — errar essa escolha deixa o contador mentindo, que é justamente o número que faz o cofre ser
 * prestação de contas.
 */
export const CHAVE_RAIZ_DO_COFRE = ["cofre"] as const;

export const CHAVE_DA_CUSTODIA = ["cofre", "custodia"] as const;

export function chaveDoCofre(implantacaoId: string) {
  return ["cofre", "implantacao", implantacaoId] as const;
}

/**
 * Com que chave mestra este portal está cifrando. Exige `console:ver`.
 *
 * `staleTime` alto porque a custódia muda quando alguém troca a configuração do host e reinicia
 * a API — não a cada navegação. `retry: false` porque a recusa aqui é informação (quem não tem
 * `console:ver` não vê o cartão), não falha de rede a insistir.
 */
export function useCustodiaDoCofre() {
  return useQuery<CustodiaDoCofre>({
    queryKey: CHAVE_DA_CUSTODIA,
    queryFn: ({ signal }) => pedirJson("/cofre/custodia", esquemaCustodia, { signal }),
    staleTime: 60_000,
    retry: false,
  });
}

/** As credenciais de uma implantação, **sem valor nenhum**. Exige `infra:ver`. */
export function useCofreDaImplantacao(implantacaoId: string) {
  return useQuery<CofreDaImplantacao>({
    queryKey: chaveDoCofre(implantacaoId),
    queryFn: ({ signal }) =>
      pedirJson(
        `/implantacoes/${encodeURIComponent(implantacaoId)}/cofre`,
        esquemaCofre,
        { signal },
      ),
    enabled: implantacaoId.length > 0,
    retry: false,
  });
}

/* --------------------------------------------------- escritas — fora do react-query */

/**
 * Grava a senha de uma pergunta do formulário no cofre.
 *
 * **Não recebe id de implantação**: a implantação sai da organização da sessão, no servidor, e a
 * trilha é descoberta a partir da pergunta. Sem parâmetro não há o que forjar.
 *
 * Devolve a credencial guardada — rótulo, custódia e datas, nunca o valor. Se o valor voltasse
 * "só desta vez", esta seria uma rota de leitura sem `credencial:revelar` e sem linha em
 * `segredo_revelacao`.
 */
export function guardarCredencial(
  perguntaId: string,
  valor: string,
): Promise<SegredoDoCofre> {
  return atualizarJson(
    `/cofre/credenciais/${encodeURIComponent(perguntaId)}`,
    esquemaSegredo,
    { valor },
  );
}

/** Descarta a credencial da pergunta **e** apaga a resposta. É o cliente limpando o campo dele. */
export function limparCredencial(perguntaId: string): Promise<void> {
  return apagar(`/cofre/credenciais/${encodeURIComponent(perguntaId)}`);
}

/**
 * A RETENÇÃO: zera o material cifrado de uma credencial que já não precisa ficar guardada.
 *
 * É outra coisa do que `limparCredencial`, e a diferença não é de implementação. Aqui quem age é
 * quem OPERA o cofre (`credencial:revelar`), a resposta do formulário **fica** — ela é o registro
 * de que o cliente entregou a credencial naquele dia — e o histórico de revelações fica junto. Lá
 * quem age é quem respondeu, com a trilha ainda editável, e a resposta some.
 *
 * Não devolve nada: a linha continua na listagem, marcada como descartada. Sumir com ela
 * esconderia exatamente a credencial que existiu, foi usada e foi apagada.
 *
 * O que o descarte **não** faz é apagar o passado do banco: `UPDATE` no Postgres escreve tupla
 * nova e a antiga sobrevive até o `VACUUM`, e mais tempo ainda em WAL, réplica e backup. Enquanto
 * a chave mestra for a mesma, quem tiver um backup e a chave ainda abre o que foi descartado. É o
 * limite de fazer descarte zerando coluna, e está escrito aqui para ninguém prometer mais.
 */
export function descartarSegredo(segredoId: string): Promise<void> {
  return apagar(`/cofre/segredos/${encodeURIComponent(segredoId)}`);
}

/**
 * Revela o valor de uma credencial. **Cria um recurso** — a revelação vira linha.
 *
 * É `POST /revelacoes` e não `GET /valor` do lado do servidor pelo mesmo motivo que aqui não é
 * `useQuery`: não existe leitura sem efeito. Cada chamada desta função é uma linha nova em
 * `segredo_revelacao` com o usuário, o motivo e o IP.
 */
export function revelarSegredo(segredoId: string, motivo: string): Promise<ValorRevelado> {
  return enviarJson(
    `/cofre/segredos/${encodeURIComponent(segredoId)}/revelacoes`,
    esquemaValorRevelado,
    { motivo },
  );
}

/**
 * Estado de uma escrita do cofre: em andamento, recusada, ou nem uma coisa nem outra.
 *
 * É o substituto de `useMutation` para tudo o que carrega senha — ver a primeira regra do
 * cabeçalho. O que ele guarda é um booleano e uma frase de erro; o valor nunca passa por aqui.
 */
export interface EscritaDoCofre {
  executando: boolean;
  erro: string | null;
  limparErro: () => void;
  /** `true` quando a tarefa terminou sem recusa. */
  executar: (tarefa: () => Promise<unknown>) => Promise<boolean>;
}

export function useEscritaDoCofre(): EscritaDoCofre {
  const [executando, setExecutando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const executar = useCallback(async (tarefa: () => Promise<unknown>): Promise<boolean> => {
    setExecutando(true);
    setErro(null);
    try {
      await tarefa();
      return true;
    } catch (causa) {
      setErro(mensagemDoCofre(causa));
      return false;
    } finally {
      setExecutando(false);
    }
  }, []);

  const limparErro = useCallback(() => setErro(null), []);

  return { executando, erro, limparErro, executar };
}

/**
 * A recusa do cofre em uma frase.
 *
 * As mensagens do serviço são escritas para quem está do outro lado da tela — "esta credencial
 * foi descartada", "a chave mestra deste portal não responde" —, então elas passam inteiras. A
 * frase genérica só entra quando não veio envelope nenhum (queda de rede, 500).
 */
export function mensagemDoCofre(causa: unknown): string {
  if (causa instanceof ErroDaApi) {
    if (causa.status === null) {
      return "Não foi possível falar com o portal. Verifique a conexão e tente de novo.";
    }
    return causa.message;
  }
  return "Não foi possível concluir a operação no cofre agora. Tente de novo em instantes.";
}

/* ------------------------------------------------------------- rótulos e formatação */

/** O caminho da tela do cofre de uma implantação. Um lugar só, para as telas apontarem. */
export function caminhoDoCofre(implantacaoId: string): string {
  return `/admin/implantacoes/${encodeURIComponent(implantacaoId)}/cofre`;
}

export const ROTULO_DA_CUSTODIA: Record<EstadoDaCustodia, string> = {
  desligado: "Cofre desligado",
  provisoria: "Chave provisória",
  gerenciada: "Chave definitiva",
  indisponivel: "Cofre parado",
};

/** A custódia de uma linha, em palavra de gente. O enum do banco não é texto de tela. */
export const ROTULO_DA_CUSTODIA_DO_SEGREDO: Record<CustodiaDoSegredo, string> = {
  gerenciada: "definitiva",
  provisoria: "provisória",
};

/** A mesma escala do design system — nenhum tom novo inventado para esta tela. */
export const CHIP_DA_CUSTODIA: Record<
  EstadoDaCustodia,
  "ok" | "atencao" | "erro" | "neutro"
> = {
  desligado: "neutro",
  provisoria: "atencao",
  gerenciada: "ok",
  indisponivel: "erro",
};

/**
 * O que cada estado significa para quem administra — e o que fazer a respeito.
 *
 * A frase da `provisoria` é a razão de esta fatia existir com um adaptador em vez de uma chave
 * fixa: a spec §5.4 promete que o host do portal **nunca** tem a chave mestra em arquivo, e hoje
 * ele tem. Um cofre que parece seguro e não é é pior que um campo de texto, porque quem usa
 * passa a confiar. O `detalhe` que vem do servidor é acrescentado a isto, nunca substituído.
 */
export const TEXTO_DA_CUSTODIA: Record<EstadoDaCustodia, string> = {
  desligado:
    "O cofre ainda não foi ativado, então nenhuma credencial é guardada nem revelada: o " +
    "questionário de infraestrutura não tem onde receber as senhas do cliente. Fale com a " +
    "equipe responsável pelo portal.",
  provisoria:
    "As credenciais dos clientes estão protegidas por uma chave provisória, guardada no " +
    "próprio servidor: quem comprometer o servidor alcança todas elas. Quando a chave " +
    "definitiva for ativada, todas as credenciais guardadas até lá são protegidas de novo.",
  gerenciada: "As credenciais dos clientes estão protegidas pela chave definitiva, fora do servidor.",
  indisponivel:
    "Nenhuma credencial pode ser guardada nem revelada agora, e a etapa de infraestrutura fica " +
    "parada até o cofre voltar. Avise a equipe responsável pelo portal.",
};

const FORMATO_DE_INSTANTE = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "America/Sao_Paulo",
});

/**
 * `2026-08-28T17:03:11+00:00` → `28/08/2026 14:03`.
 *
 * Data **e hora**, ao contrário de `formatarData`: quantas vezes uma credencial foi vista hoje é
 * a pergunta que a auditoria responde, e ela não cabe em granularidade de dia. O fuso é fixo em
 * São Paulo pelo mesmo motivo do resto do portal — o consultor e o cliente precisam ler a mesma
 * hora, não a hora do navegador de cada um.
 */
export function formatarInstante(iso: string | null): string | null {
  if (!iso) return null;
  const momento = new Date(iso);
  return Number.isNaN(momento.getTime()) ? null : FORMATO_DE_INSTANTE.format(momento);
}

/** Quem guardou e quando, em uma linha. `rotacionado_*` vence porque é o valor que vale hoje. */
export function ultimaGravacao(segredo: SegredoDoCofre): {
  quando: string | null;
  quem: string | null;
  rotacionado: boolean;
} {
  const rotacionado = segredo.rotacionado_em !== null;
  return {
    quando: formatarInstante(rotacionado ? segredo.rotacionado_em : segredo.criado_em),
    quem: (rotacionado ? segredo.rotacionado_por : segredo.criado_por)?.nome ?? null,
    rotacionado,
  };
}
